export function parseHHMM(s: string): { h: number; m: number } {
  const [hh, mm] = s.split(':').map(Number)
  return { h: hh, m: mm }
}

export function combineDateAndTime(date: string | Date, time: string): string {
  const d = date instanceof Date ? date : new Date(date)
  const [hours, minutes] = time.split(':').map(Number)

  d.setHours(hours, minutes, 0, 0)

  return d.toISOString()
}
export const toIsoLocalZ = (d: Date) =>
  new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString()

export function utcDayIso(date = new Date()) {
  const y = date.getUTCFullYear()
  const m = String(date.getUTCMonth() + 1).padStart(2, '0')
  const d = String(date.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${d}` // YYYY-MM-DD
}

export function buildTimeOptions(start = 6, end = 19, stepMin = 30) {
  const out: { value: string; label: string }[] = []
  for (let h = start; h <= end; h++) {
    for (let m = 0; m < 60; m += stepMin) {
      if (h === end && m > 0) break
      const hour12 = ((h + 11) % 12) + 1
      const ampm = h < 12 ? 'AM' : 'PM'
      out.push({
        value: `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`,
        label: `${hour12}:${String(m).padStart(2, '0')} ${ampm}`,
      })
    }
  }
  return out
}
export const getNextTimeSlotEnding = (start: string) => {
  const timeOptions = buildTimeOptions(8, 20, 30)
  const startIndex = timeOptions.findIndex((opt) => opt.value === start)
  if (startIndex === -1 || startIndex === timeOptions.length - 1)
    return timeOptions[timeOptions.length - 1].value // Default end time if not found or last option
  return timeOptions[startIndex + 1].value // Next time slot ending
}
export function updateDateTime24(date: Date | string, timeStr: string): string {
  date = date instanceof Date ? date : new Date(date)

  // Parse "HH:mm"
  const match = timeStr.match(/^(\d{1,2}):(\d{2})$/)
  if (!match) throw new Error('Invalid time format. Expected HH:mm')

  const hours = parseInt(match[1], 10)
  const minutes = parseInt(match[2], 10)

  // Update time
  date.setHours(hours, minutes, 0, 0)

  return date.toISOString()
}
export function isMultipleDayRange(startIso: string, endIso: string): boolean {
  const start = new Date(startIso)
  const end = new Date(endIso)

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    throw new Error('Invalid ISO date string(s).')
  }

  return (
    start.getUTCFullYear() !== end.getUTCFullYear() ||
    start.getUTCMonth() !== end.getUTCMonth() ||
    start.getUTCDate() !== end.getUTCDate()
  )
}
type DatePair = { start: string; end: string }

/**
 * Splits a [start, end] range into daily pairs.
 * - Keeps the *time-of-day* from the original start for every day's "start"
 * - Keeps the *time-of-day* from the original end for every day's "end"
 * - Produces one pair per UTC day from startDate..endDate (inclusive)
 *
 * Example:
 * 2026-01-30T07:00Z .. 2026-01-31T15:00Z
 * =>
 * [
 *  { start: 2026-01-30T07:00Z, end: 2026-01-30T15:00Z },
 *  { start: 2026-01-31T07:00Z, end: 2026-01-31T15:00Z }
 * ]
 */
export function buildDailyPairs(startIso: string, endIso: string): DatePair[] {
  const start = new Date(startIso)
  const end = new Date(endIso)

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    throw new Error('Invalid ISO date string(s).')
  }

  // If end is before start, you can choose: throw, swap, or return []
  if (end.getTime() < start.getTime()) {
    throw new Error('endIso must be >= startIso.')
  }

  const startTime = {
    h: start.getUTCHours(),
    m: start.getUTCMinutes(),
    s: start.getUTCSeconds(),
    ms: start.getUTCMilliseconds(),
  }

  const endTime = {
    h: end.getUTCHours(),
    m: end.getUTCMinutes(),
    s: end.getUTCSeconds(),
    ms: end.getUTCMilliseconds(),
  }

  // Iterate by UTC date (midnight boundaries in UTC)
  const cur = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate()))
  const last = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate()))

  const out: DatePair[] = []

  while (cur.getTime() <= last.getTime()) {
    const dayY = cur.getUTCFullYear()
    const dayM = cur.getUTCMonth()
    const dayD = cur.getUTCDate()

    const dayStart = new Date(
      Date.UTC(dayY, dayM, dayD, startTime.h, startTime.m, startTime.s, startTime.ms),
    )
    const dayEnd = new Date(Date.UTC(dayY, dayM, dayD, endTime.h, endTime.m, endTime.s, endTime.ms))

    out.push({
      start: dayStart.toISOString(),
      end: dayEnd.toISOString(),
    })

    // next UTC day
    cur.setUTCDate(cur.getUTCDate() + 1)
  }

  return out
}
/**
 * Returns true if the given time today is already in the past.
 * Expects format like "2:00 PM"
 */
export const isTimePast = (time: string): boolean => {
  const now = new Date()

  const target = new Date(`${now.toDateString()} ${time}`)

  return target.getTime() < now.getTime()
}
type Period = 'AM' | 'PM'

export const timeToMinutes = (time: string): number => {
  const match = time.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i)

  if (!match) {
    throw new Error(`Invalid time format: ${time}`)
  }

  let hours = Number(match[1])
  const minutes = Number(match[2])
  const seconds = Number(match[3] ?? '0')
  const periodRaw = match[4]

  if (!Number.isFinite(hours) || !Number.isFinite(minutes) || !Number.isFinite(seconds)) {
    throw new Error(`Invalid time numbers: ${time}`)
  }

  if (minutes < 0 || minutes > 59 || seconds < 0 || seconds > 59) {
    throw new Error(`Invalid minutes/seconds range: ${time}`)
  }

  // Convert 12h -> 24h if AM/PM present
  if (periodRaw) {
    const period = periodRaw.toUpperCase() as Period

    if (hours < 1 || hours > 12) {
      throw new Error(`Invalid hour for 12h format: ${time}`)
    }

    if (period === 'AM' && hours === 12) hours = 0
    if (period === 'PM' && hours !== 12) hours += 12
  } else {
    // 24h format validation
    if (hours < 0 || hours > 23) {
      throw new Error(`Invalid hour for 24h format: ${time}`)
    }
  }

  return hours * 60 + minutes + seconds / 60
}

export const isEarlier = (t1: string, t2: string): boolean => timeToMinutes(t1) < timeToMinutes(t2)
